<script setup lang="ts">
/**
 * The right-hand review panel, opened from the Save flow or the top-bar pill.
 *
 * Two tabs: Recommendations (grouped, collapsible) and Insights, the
 * configuration snapshot. It does not gate saving — errors already did that —
 * but it does carry Cancel and Save, so the user can act on what they just
 * read without going back to the header.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  ChevronDownIcon,
  InfoCircleIcon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UICheckbox, UITooltip } from "@gohighlevel/ghl-ui";
import { useProductNoun } from "@/composition/useProductNoun";
import { useAppStore } from "@/store/app";
import { useBuilderValidation } from "@/composition/useBuilderValidation";
import { groupResults } from "@/validation/grouping";
import type { GroupId } from "@/validation/grouping";
import { recommendationAction, recommendationMessage } from "@/validation/messages";
import { assetTypeOf, buildContext } from "@/validation";
import { buildInsights } from "@/validation/insights";
import type { Insight } from "@/validation/insights";
import type { ContextSource } from "@/validation";
import type { RuleResult } from "@/validation";

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{
  (event: "update:show", value: boolean): void;
}>();

const { t } = useI18n();
const store = useAppStore();
const { productParams } = useProductNoun();

/**
 * Clicking anywhere outside dismisses the panel.
 *
 * The listener is attached only while the panel is open, and the pill that
 * opens it is excluded so its own toggle is not undone by this handler firing
 * first. Attaching after the DOM settles keeps the opening click — which has
 * already finished propagating by then — from closing the panel immediately.
 */
/**
 * The panel hangs below the builder header rather than covering it, so Preview
 * and Save stay reachable while it is open.
 *
 * Measured rather than hardcoded, because the header's height is content-driven.
 * Applied as an inline style rather than through a CSS v-bind: the variable is
 * only written once the root exists, which is a render too late for a panel
 * whose root appears and disappears with `show`.
 */
const headerOffset = ref("0px");

/**
 * The builder pages mount the header as `<BuilderHeader id="builder-header">`,
 * and that attribute falls through onto the component's root, replacing the
 * `hl-builder-header` id written in its own template. Both are tried, then the
 * toolbar itself, so the panel still lands correctly if that wiring changes.
 */
const HEADER_SELECTORS = [
  "#builder-header",
  "#hl-builder-header",
  ".header-toolbar",
];

const measureHeaderOffset = (): void => {
  for (const selector of HEADER_SELECTORS) {
    const header = document.querySelector(selector);
    // The bottom edge, not the height: it is already viewport-relative, which
    // is what a fixed panel's `top` needs.
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    if (bottom > 0) {
      headerOffset.value = `${bottom}px`;
      return;
    }
  }
  headerOffset.value = "0px";
};

const PANEL_SELECTOR = "#builder-review-panel";
const TRIGGER_SELECTOR = "#builder-recommendations-pill, #save-action, #builder-save-error-trigger";

const handleOutsideClick = (event: MouseEvent): void => {
  const node = event.target as HTMLElement | null;
  if (!node?.closest) return;
  if (node.closest(PANEL_SELECTOR) || node.closest(TRIGGER_SELECTOR)) return;
  emit("update:show", false);
};

const stopWatchingOutsideClicks = (): void =>
  document.removeEventListener("click", handleOutsideClick);

watch(
  () => props.show,
  async (open) => {
    stopWatchingOutsideClicks();
    if (!open) return;
    measureHeaderOffset();
    await nextTick();
    document.addEventListener("click", handleOutsideClick);
  }
);

onBeforeUnmount(stopWatchingOutsideClicks);
// The panel renders whatever the last run produced; deciding when to run is the
// caller's job (on save, and when the pill opens it). Keeping that out of here
// leaves the panel presentational.
const { recommendations, focusResult, focusSettingsTab, focusPaymentElement } =
  useBuilderValidation();

type Tab = "recommendations" | "insights";
const activeTab = ref<Tab>("recommendations");
const collapsed = ref<Set<string>>(new Set());

const groups = computed(() =>
  groupResults(
    assetTypeOf(store as unknown as ContextSource),
    recommendations.value
  )
);

const total = computed(() => recommendations.value.length);

// Derived from the same snapshot the rules read, so the two tabs can never
// disagree about the asset.
const insights = computed(() =>
  props.show ? buildInsights(buildContext(store as unknown as ContextSource)) : []
);

/** QA selector per group. Page-based quiz groups use the shorter PRD names. */
const groupElementId = (groupId: GroupId): string => {
  const slug =
    groupId === "questionsPage"
      ? "questions"
      : groupId === "resultsPage"
        ? "results"
        : groupId;
  return `builder-${slug}-recommendations`;
};

const insightElementId = (insightId: string): string =>
  `builder-detail-${insightId.replace(/_/g, "-")}`;

/**
 * Opens the surface an insight describes, and closes the panel behind it —
 * the user is being sent somewhere, so leaving this over the top of it hides
 * what they were sent to see. Rows with no owning surface carry no control.
 */
const openInsight = (insight: Insight): void => {
  if (!insight.settingsTab) return;
  // Payment settings live in the payment element's own sidebar rather than on
  // a tab, so that element has to be selected instead.
  if (insight.settingsTab === "payments") focusPaymentElement();
  else focusSettingsTab(insight.settingsTab);
  emit("update:show", false);
};

const isCollapsed = (groupId: string): boolean => collapsed.value.has(groupId);

// The panel opens on its first group only. Showing every group expanded buries
// the first one under the others, and the list is a work queue — the user wants
// somewhere to start, not the whole thing at once.
watch(
  () => props.show,
  (open) => {
    if (!open) return;
    collapsed.value = new Set(groups.value.slice(1).map((group) => group.id));
  },
  { immediate: true }
);

const toggle = (groupId: string): void => {
  const next = new Set(collapsed.value);
  if (next.has(groupId)) next.delete(groupId);
  else next.add(groupId);
  collapsed.value = next;
};

const messageFor = (result: RuleResult): string => recommendationMessage(result);
const actionFor = (result: RuleResult): string =>
  recommendationAction(result);

/**
 * Every finding is actionable: element-bound ones select the element, and
 * settings-bound ones switch to the tab that owns the setting.
 */
const jumpTo = (result: RuleResult): void => {
  focusResult(result);
  emit("update:show", false);
};

/**
 * Cancel closes the review without saving. Builder edits are untouched — this
 * cancels the save action, not the work.
 */
const close = (): void => emit("update:show", false);

/**
 * Bound to the setting itself rather than held locally.
 *
 * There is no Save in this panel to commit a pending tick against, so the box
 * applies as it is ticked — and reads back the real state, so reopening the
 * panel never shows a tick the asset does not have.
 */
const dontShowAgain = computed({
  get: () => store.builderConfig.showRecommendationsBeforeSaving === false,
  set: (value: boolean) => {
    store.builderConfig.showRecommendationsBeforeSaving = !value;
    store.anyUnsavedChanges = true;
  },
});
</script>

<template>
  <aside
    v-if="props.show"
    id="builder-review-panel"
    class="review-panel"
    :style="{ top: headerOffset }"
    :aria-label="t('builderValidation.ui.panelLabel')"
  >
    <header class="panel-head">
      <div class="min-w-0 flex-1">
        <p class="panel-title">
          {{ t("builderValidation.ui.panelTitle", { n: total }) }}
        </p>
        <p class="panel-subtitle">
          {{ t("builderValidation.ui.panelSubtitle", productParams) }}
        </p>
      </div>
      <button
        id="builder-review-panel-close"
        type="button"
        class="panel-close"
        :aria-label="t('common.close')"
        @click="close"
      >
        <XCloseIcon class="h-4 w-4" />
      </button>
    </header>

    <div class="panel-tabs" role="tablist">
      <button
        id="builder-tab-recommendations"
        type="button"
        role="tab"
        class="panel-tab"
        :class="{ 'is-active': activeTab === 'recommendations' }"
        :aria-selected="activeTab === 'recommendations'"
        @click="activeTab = 'recommendations'"
      >
        {{ t("builderValidation.ui.tabRecommendations") }}
      </button>
      <button
        id="builder-tab-insights"
        type="button"
        role="tab"
        class="panel-tab"
        :class="{ 'is-active': activeTab === 'insights' }"
        :aria-selected="activeTab === 'insights'"
        @click="activeTab = 'insights'"
      >
        {{ t("builderValidation.ui.tabInsights") }}
      </button>
    </div>

    <div v-if="activeTab === 'recommendations'" class="panel-body">
      <p v-if="!total" class="panel-empty" data-testid="builder-review-empty">
        <span class="empty-title">{{
          t("builderValidation.ui.noRecommendationsTitle")
        }}</span>
        <span class="empty-body">{{
          t("builderValidation.ui.noRecommendationsBody", productParams)
        }}</span>
      </p>

      <section
        v-for="group in groups"
        :key="group.id"
        :id="groupElementId(group.id)"
        class="reco-group"
        data-testid="builder-reco-group"
      >
        <button
          type="button"
          class="group-head"
          :aria-expanded="!isCollapsed(group.id)"
          @click="toggle(group.id)"
        >
          <ChevronDownIcon
            class="h-4 w-4 transition-transform"
            :class="{ '-rotate-90': isCollapsed(group.id) }"
          />
          <span class="group-label">{{ group.label }}</span>
          <span class="group-count">{{ group.results.length }}</span>
        </button>

        <ul v-if="!isCollapsed(group.id)" class="group-list">
          <li
            v-for="(result, position) in group.results"
            :key="result.key"
            class="reco-item"
            data-testid="builder-reco-item"
          >
            <span class="item-index" aria-hidden="true">{{ position + 1 }}</span>
            <div class="min-w-0 flex-1">
              <p class="item-message">{{ messageFor(result) }}</p>
              <button type="button" class="item-action" @click="jumpTo(result)">
                {{ actionFor(result) }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <div v-else class="panel-body">
      <p class="insights-heading">
        {{ t("builderValidation.insights.heading") }}
      </p>
      <ul id="builder-details-section" class="insight-list">
        <li
          v-for="insight in insights"
          :id="insightElementId(insight.id)"
          :key="insight.id"
          class="insight-row"
          data-testid="builder-insight-row"
        >
          <span class="insight-label">{{ insight.label }}</span>
          <span
            class="insight-value"
            :class="{ 'is-warning': insight.tone === 'warning' }"
          >
            <span v-if="insight.tone === 'warning'" class="insight-dot" />
            {{ insight.value }}
          </span>
          <button
            v-if="insight.settingsTab"
            type="button"
            class="insight-open"
            :aria-label="t('builderValidation.ui.openInsight', { name: insight.label })"
            @click="openInsight(insight)"
          >
            <InfoCircleIcon class="h-4 w-4" />
          </button>
        </li>
      </ul>
    </div>

    <footer class="panel-actions">
      <div class="dont-show">
        <UICheckbox
          id="builder-recommendations-dont-show"
          :checked="dontShowAgain"
          :label="t('builderValidation.ui.dontShowAgain')"
          @update:checked="dontShowAgain = $event"
        />
        <!-- The checkbox turns the feature off rather than dismissing this one
             panel, which is worth saying before it is ticked. -->
        <UITooltip trigger="hover" placement="top-end">
          <template #trigger>
            <span id="builder-recommendations-dont-show-hint" class="dont-show-hint">
              <InfoCircleIcon class="h-4 w-4 text-gray-400" />
            </span>
          </template>
          <template #default>
            <span class="dont-show-hint-text">
              {{ t("builderValidation.ui.dontShowAgainHint") }}
            </span>
          </template>
        </UITooltip>
      </div>
    </footer>
  </aside>
</template>

<style scoped>
.review-panel {
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 55;
  width: 440px;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #eaecf0;
  box-shadow:
    0 8px 8px -4px rgb(16 24 40 / 3%),
    0 20px 24px -4px rgb(16 24 40 / 8%);
  text-align: left;
}
/* Bootstrap loads after Tailwind and re-enables the native button appearance.
   Only `appearance` is reset globally here — every other property is declared
   per button below, because a selector broad enough to catch them all also
   outranks the rules that style them. */
.review-panel button {
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}
.panel-head {
  display: flex;
  gap: 12px;
  padding: 16px;
}
.panel-title {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #101828;
}
.panel-subtitle {
  margin: 2px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.panel-close {
  background: none;
  border: none;
  padding: 0;
  color: #667085;
  align-self: flex-start;
}
.panel-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin: 0 16px;
  padding: 4px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}
.panel-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #475467;
}
.panel-tab.is-active {
  background: #fff;
  color: #155eef;
  border: 1px solid #eaecf0;
  box-shadow: 0 1px 2px rgb(16 24 40 / 5%);
}
.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.panel-empty {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 24px 8px;
  text-align: center;
}
.empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}
.empty-body {
  font-size: 13px;
  color: #475467;
}
.reco-group {
  margin-bottom: 12px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  overflow: hidden;
}
.group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: #f9fafb;
  color: #101828;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
}
.group-count {
  margin-left: auto;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9999px;
  background: #fffaeb;
  color: #b54708;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
}
.group-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.reco-item {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-top: 1px solid #f2f4f7;
}
.item-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: #f2f4f7;
  color: #475467;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}
.item-message {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}
.item-action {
  margin-top: 2px;
  padding: 0;
  background: none;
  border: none;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: #004eeb;
}
.item-action:hover {
  text-decoration: underline;
}
.insights-heading {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 500;
  color: #101828;
}
.insight-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  overflow: hidden;
}
.insight-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #f2f4f7;
}
.insight-row:last-child {
  border-bottom: 0;
}
.dont-show-hint {
  display: inline-flex;
  align-items: center;
  /* Centres the icon against the checkbox label rather than the taller row. */
  line-height: 0;
  margin-left: 2px;
  cursor: pointer;
}

/* The tooltip body is teleported out, but scoped styles travel with it because
   the attribute is on the element. Without a width it renders as one long line
   that runs off the left of the panel. */
.dont-show-hint-text {
  display: inline-block;
  max-width: 240px;
  white-space: normal;
}
.insight-open {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: none;
  color: #98a2b3;
  cursor: pointer;
}
.insight-open:hover {
  color: #155eef;
}
.insight-label {
  flex: 1;
  font-size: 14px;
  color: #475467;
}
.insight-value {
  font-size: 14px;
  font-weight: 500;
  color: #101828;
}
/* Test mode reads as configured while collecting no money, so it is the one
   value the snapshot flags rather than simply reports. */
.insight-value.is-warning {
  padding: 2px 8px;
  border-radius: 9999px;
  background: #fef3f2;
  color: #b42318;
  font-size: 12px;
}
/* The warning value reads as a status pill with a leading dot. */
.insight-value.is-warning {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.insight-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: currentcolor;
}
.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eaecf0;
}
.dont-show {
  display: flex;
  align-items: center;
  gap: 0;
  margin-right: auto;
  font-size: 14px;
  color: #344054;
  cursor: pointer;
}
</style>
